import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

// 3.20.3 — Forza scroll-to-top al cambio rotta + al primo load (fix refresh che restava in mezzo)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import DifferentiatorsSection from './components/DifferentiatorsSection';
import HeadlessSection from './components/HeadlessSection';
import PricingSection from './components/PricingSection';
import FreeWordPressOrgSection from './components/FreeWordPressOrgSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import ReferralLeaderboard from './components/ReferralLeaderboard';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import UserDashboard from './components/UserDashboard';
import AdminDashboard from './components/AdminDashboard';
import CheckoutSuccessPage from './components/CheckoutSuccessPage';
import OnboardingWizard from './components/OnboardingWizard';
import AIDemoSection from './components/AIDemoSection';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import CookiePolicy from './components/CookiePolicy';
import VerifyEmailPage from './components/VerifyEmailPage';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import AEOSection from './components/AEOSection';
import ComparisonTable from './components/ComparisonTable';
import ProblemSection from './components/ProblemSection';
// 3.30 — Conversion-focused storytelling sections
import UseCasesSection from './components/UseCasesSection';
import MigrationStorySection from './components/MigrationStorySection';
import AIEnginesSection from './components/AIEnginesSection';
import SavingsCalculator from './components/SavingsCalculator';
import NativeStackSection from './components/NativeStackSection';
// 3.26.5 — Guide pages + Changelog
import GuidesIndexPage from './components/GuidesIndexPage';
import GuideWpPage from './components/GuideWpPage';
import ChangelogPage from './components/ChangelogPage';
import RedeemPage from './components/RedeemPage';
import WaiverReceiptPage from './components/WaiverReceiptPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};


// Landing Page Component
const LandingPage = () => {
  return (
    <>
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <AIDemoSection />
        <UseCasesSection />
        <DifferentiatorsSection />
        <HeadlessSection />
        <AIEnginesSection />
        <AEOSection />
        <NativeStackSection />
        <FeaturesSection />
        <MigrationStorySection />
        <ComparisonTable />
        <SavingsCalculator />
        <PricingSection />
        <FreeWordPressOrgSection />
        <TestimonialsSection />
        <ReferralLeaderboard />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </>
  );
};

// Pricing Page (standalone)
const PricingPage = () => {
  return (
    <>
      <Header />
      <main className="pt-20">
        <PricingSection />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
};

// App Content with Routes
const AppContent = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Header />
            <UserDashboard />
            <Footer />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      {/* 3.26.5 — Guide pages + Changelog */}
      <Route path="/guide" element={<GuidesIndexPage />} />
      {/* 2026-07-14 — guide dinamiche dal CPT WP (REST /wp/v2/guides). Slug Polylang
          univoci per lingua → fetch by-slug, lang dal prefisso URL.
          2026-07-17 — rimosse le 5 route statiche legacy (GuideLlmsTxt & co.):
          oscuravano il CPT per quegli slug e il client mostrava il contenuto i18n
          congelato di maggio SENZA le FAQ del batch — mentre l'SSR serviva il CPT
          con FAQ (0 <details> nel DOM idratato, collaudo 2026-07-17). Stessa classe
          dello shadowing nginx risolto il 16/07, ma nel router client. */}
      <Route path="/guide/:slug" element={<GuideWpPage />} />
      <Route path="/:lang/guide/:slug" element={<GuideWpPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/redeem" element={<RedeemPage />} />
      <Route path="/waiver-receipt/:licenseId" element={<ProtectedRoute><WaiverReceiptPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const SentryFallback = ({ resetError }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050505', color: '#fff', padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
    <div style={{ maxWidth: 480, textAlign: 'center' }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Errore inatteso</h1>
      <p style={{ opacity: 0.7, marginBottom: '1.5rem' }}>Qualcosa non ha funzionato. Il problema è stato segnalato al team.</p>
      <button onClick={resetError} style={{ padding: '0.75rem 1.5rem', background: '#0055FF', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '1rem' }}>
        Riprova
      </button>
    </div>
  </div>
);

const routerBasename = (() => {
  const publicUrl = process.env.PUBLIC_URL || '';
  if (!publicUrl || publicUrl === '.') {
    return undefined;
  }

  try {
    const pathname = publicUrl.startsWith('http') ? new URL(publicUrl).pathname : publicUrl;
    const normalized = pathname.replace(/\/+$/, '');
    return normalized && normalized !== '/' ? normalized : undefined;
  } catch (error) {
    const normalized = publicUrl.replace(/\/+$/, '');
    return normalized && normalized !== '/' ? normalized : undefined;
  }
})();

function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
      <Router basename={routerBasename}>
        <ScrollToTop />
        <AuthProvider>
          <div className="App min-h-screen bg-[#050505]">
            <AppContent />
            <FloatingWhatsApp />
          </div>
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default App;
